import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calculator,
  Play,
  Home,
  ChevronRight,
  Shield,
  Edit,
  CheckSquare,
  FileCheck,
  Check,
  AlertTriangle,
  Users,
  DollarSign,
  Clock,
} from 'lucide-react';
import { ComingSoonBanner } from '../ComingSoonBanner';

interface Step {
  id: number;
  title: string;
  icon: string;
}

const steps: Step[] = [
  { id: 1, title: 'Pre-Payroll Validation', icon: 'Shield' },
  { id: 2, title: 'Adjustments', icon: 'Edit' },
  { id: 3, title: 'Review & Approval', icon: 'CheckSquare' },
  { id: 4, title: 'Finalization', icon: 'FileCheck' },
];

const getStepIcon = (iconName: string, size: string = 'w-4 h-4') => {
  switch (iconName) {
    case 'Shield':
      return <Shield className={size} />;
    case 'Edit':
      return <Edit className={size} />;
    case 'CheckSquare':
      return <CheckSquare className={size} />;
    case 'FileCheck':
      return <FileCheck className={size} />;
    default:
      return <Shield className={size} />;
  }
};

// Mock validation data for preview
const mockValidationItems = [
  { id: 1, type: 'Missing IBAN', employee: 'Ahmad Hassan', severity: 'error' },
  { id: 2, type: 'Pending Leave Approval', employee: 'Sarah Ali', severity: 'warning' },
  { id: 3, type: 'Attendance Gap', employee: 'Omar Khalil', severity: 'warning' },
];

export default function PayrollRunWizard() {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <ComingSoonBanner
        icon={<Calculator className="w-6 h-6" />}
        title="Payroll Run Wizard"
        description="Payroll Run Wizard is currently in development. This is a preview of the upcoming feature."
        gradientFrom="from-violet-500"
        gradientTo="to-purple-500"
      />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg mr-4">
            <Play className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('Payroll Run Wizard')}</h1>
            <p className="text-gray-600">{t('Guided payroll processing workflow')}</p>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      <div className="mb-8">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-4">
            <li>
              <div className="flex items-center">
                <Home className="w-4 h-4 text-gray-700" />
                <span className="ml-2 text-sm font-medium text-gray-500">{t('Payroll')}</span>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRight className="w-4 h-4 text-gray-700 mx-2" />
                <span className="text-sm font-medium text-gray-900">{t('Run Payroll')}</span>
              </div>
            </li>
          </ol>
        </nav>
      </div>

      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="bg-card rounded-xl shadow-sm border border-border p-6">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center flex-1">
                <button
                 
                  className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                    currentStep === step.id
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : currentStep > step.id
                      ? 'bg-green-600 border-green-600 text-white'
                      : 'bg-card border-border text-gray-700'
                  }`}
                 
                >
                  {currentStep > step.id ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    getStepIcon(step.icon)
                  )}
                </button>
                <div className="ml-3">
                  <p
                    className={`text-sm font-medium ${
                      currentStep >= step.id ? 'text-gray-900' : 'text-gray-700'
                    }`}
                  >
                    {t('Step')} {step.id}
                  </p>
                  <p
                    className={`text-xs ${
                      currentStep >= step.id ? 'text-gray-600' : 'text-gray-700'
                    }`}
                  >
                    {t(step.title)}
                  </p>
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-6 ${
                      currentStep > step.id ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-xs text-gray-600 mb-2">
              <span>{t('Progress')}</span>
              <span>{Math.round((currentStep / steps.length) * 100)}% {t('Complete')}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Step Content - Pre-Payroll Validation Preview */}
      <div className="bg-card rounded-xl shadow-sm border border-border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">{t('Pre-Payroll Validation')}</h2>
          </div>
          <button
           
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg"
           
          >
            {t('Run Validation')}
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <span className="text-sm font-medium text-red-800">{t('Errors')}</span>
            </div>
            <p className="text-2xl font-bold text-red-900">1</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-5 h-5 text-yellow-500" />
              <span className="text-sm font-medium text-yellow-800">{t('Warnings')}</span>
            </div>
            <p className="text-2xl font-bold text-yellow-900">2</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-5 h-5 text-green-500" />
              <span className="text-sm font-medium text-green-800">{t('Employees Ready')}</span>
            </div>
            <p className="text-2xl font-bold text-green-900">122</p>
          </div>
        </div>

        {/* Validation Items */}
        <div className="space-y-3">
          {mockValidationItems.map((item) => (
            <div
              key={item.id}
              className={`flex items-center justify-between p-3 rounded-lg ${
                item.severity === 'error' ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'
              }`}
            >
              <div className="flex items-center gap-3">
                <AlertTriangle
                  className={`w-4 h-4 ${
                    item.severity === 'error' ? 'text-red-500' : 'text-yellow-500'
                  }`}
                />
                <div>
                  <p className="text-sm font-medium text-gray-900">{t(item.type)}</p>
                  <p className="text-xs text-gray-500">{item.employee}</p>
                </div>
              </div>
              <button
               
                className="text-xs px-3 py-1 bg-card border border-border rounded text-gray-700"
               
              >
                {t('Resolve')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-end gap-3">
        <button
         
          className="px-6 py-2 bg-blue-500 text-white rounded-lg"
         
        >
          {t('Next Step')}
        </button>
      </div>
    </div>
  );
}
